/**
 * Storm banner engine
 * - Multi-depth parallax rain (far blurred/slow, mid, near sharp/fast)
 * - Glass lens beads with drip
 * - Splash bursts at impact
 * - Thunder: sky dims → jagged bolt → flash → screen shake
 */
(function () {
  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function sleep(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  function setupCanvas(canvas) {
    var ctx = canvas.getContext('2d');
    if (!ctx) return null;
    var dpr = 1;

    function resize() {
      var parent = canvas.parentElement;
      if (!parent) return { w: 0, h: 0 };
      var rect = parent.getBoundingClientRect();
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.max(1, Math.floor(rect.width * dpr));
      canvas.height = Math.max(1, Math.floor(rect.height * dpr));
      canvas.style.width = rect.width + 'px';
      canvas.style.height = rect.height + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      return { w: rect.width, h: rect.height };
    }

    return { ctx: ctx, resize: resize, canvas: canvas };
  }

  function makeDrop(w, h, depth) {
    // depth: 'far' | 'mid' | 'near'
    if (depth === 'far') {
      return {
        x: Math.random() * w,
        y: Math.random() * h,
        len: 6 + Math.random() * 12,
        speed: 3.5 + Math.random() * 4.5,
        thickness: 0.35 + Math.random() * 0.35,
        alpha: 0.1 + Math.random() * 0.14,
        wind: 0.35 + Math.random() * 0.7,
        depth: depth
      };
    }
    if (depth === 'mid') {
      return {
        x: Math.random() * w,
        y: Math.random() * h,
        len: 12 + Math.random() * 18,
        speed: 8 + Math.random() * 10,
        thickness: 0.65 + Math.random() * 0.7,
        alpha: 0.2 + Math.random() * 0.28,
        wind: 0.9 + Math.random() * 1.4,
        depth: depth
      };
    }
    return {
      x: Math.random() * w,
      y: Math.random() * h,
      len: 18 + Math.random() * 30,
      speed: 16 + Math.random() * 20,
      thickness: 1.1 + Math.random() * 1.5,
      alpha: 0.35 + Math.random() * 0.45,
      wind: 1.8 + Math.random() * 2.8,
      depth: depth,
      canSplash: true
    };
  }

  function startStormRain(overlay) {
    if (prefersReducedMotion()) return null;

    var farSetup = setupCanvas(overlay.querySelector('.banner-rain__canvas--far'));
    var midSetup = setupCanvas(overlay.querySelector('.banner-rain__canvas--mid'));
    var nearSetup = setupCanvas(overlay.querySelector('.banner-rain__canvas--near'));
    var splashSetup = setupCanvas(overlay.querySelector('.banner-rain__canvas--splash'));
    if (!farSetup || !midSetup || !nearSetup || !splashSetup) return null;

    var far = [];
    var mid = [];
    var near = [];
    var splashes = [];
    var running = true;
    var rafId = 0;
    var stormBoost = 1;
    var size = { w: 0, h: 0 };
    var isMobile = window.matchMedia('(max-width: 767px)').matches;

    function rebuild() {
      size = farSetup.resize();
      midSetup.resize();
      nearSetup.resize();
      splashSetup.resize();
      var w = size.w;
      var h = size.h;
      var farCount = Math.min(isMobile ? 55 : 95, Math.floor((w * h) / 15000));
      var midCount = Math.min(isMobile ? 40 : 70, Math.floor((w * h) / 18000));
      var nearCount = Math.min(isMobile ? 28 : 55, Math.floor((w * h) / 22000));
      far = [];
      mid = [];
      near = [];
      var i;
      for (i = 0; i < farCount; i++) far.push(makeDrop(w, h, 'far'));
      for (i = 0; i < midCount; i++) mid.push(makeDrop(w, h, 'mid'));
      for (i = 0; i < nearCount; i++) near.push(makeDrop(w, h, 'near'));
    }

    function spawnSplash(x, y) {
      var count = 5 + Math.floor(Math.random() * 5);
      for (var i = 0; i < count; i++) {
        var angle = -Math.PI + Math.random() * Math.PI;
        var speed = 1.2 + Math.random() * 3.4;
        splashes.push({
          x: x,
          y: y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed * 0.55 - (1 + Math.random() * 2),
          life: 1,
          decay: 0.03 + Math.random() * 0.025,
          size: 1 + Math.random() * 2.2
        });
      }
      // Ground ripple ring
      splashes.push({
        x: x,
        y: y,
        vx: 0,
        vy: 0,
        life: 1,
        decay: 0.045,
        size: 2,
        ring: true,
        radius: 2
      });
    }

    function drawDrops(setup, list) {
      var ctx = setup.ctx;
      var w = size.w;
      var h = size.h;
      ctx.clearRect(0, 0, w, h);
      for (var i = 0; i < list.length; i++) {
        var d = list[i];
        var alpha = Math.min(0.98, d.alpha * (0.85 + stormBoost * 0.4));
        var grad = ctx.createLinearGradient(d.x, d.y, d.x + d.wind, d.y + d.len);
        grad.addColorStop(0, 'rgba(230, 245, 255, 0)');
        grad.addColorStop(0.4, 'rgba(230, 245, 255,' + alpha + ')');
        grad.addColorStop(1, 'rgba(190, 220, 245, 0)');
        ctx.beginPath();
        ctx.strokeStyle = grad;
        ctx.lineWidth = d.thickness * (stormBoost > 1.3 ? 1.12 : 1);
        ctx.lineCap = 'round';
        ctx.moveTo(d.x, d.y);
        ctx.lineTo(d.x + d.wind, d.y + d.len);
        ctx.stroke();

        d.y += d.speed * stormBoost;
        d.x += d.wind * 0.42 * stormBoost;

        if (d.y + d.len >= h - 2) {
          if (d.canSplash && Math.random() > 0.35) {
            spawnSplash(d.x + d.wind * 0.5, h - 2);
          }
          d.y = -d.len - Math.random() * 50;
          d.x = Math.random() * w;
        }
        if (d.x > w + 30) d.x = -20;
      }
    }

    function drawSplashes() {
      var ctx = splashSetup.ctx;
      var w = size.w;
      var h = size.h;
      ctx.clearRect(0, 0, w, h);
      for (var i = splashes.length - 1; i >= 0; i--) {
        var p = splashes[i];
        p.life -= p.decay;
        if (p.life <= 0) {
          splashes.splice(i, 1);
          continue;
        }
        if (p.ring) {
          p.radius += 1.6;
          ctx.beginPath();
          ctx.strokeStyle = 'rgba(220, 240, 255,' + 0.35 * p.life + ')';
          ctx.lineWidth = 1.2;
          ctx.ellipse(p.x, p.y, p.radius, p.radius * 0.28, 0, 0, Math.PI * 2);
          ctx.stroke();
        } else {
          p.x += p.vx;
          p.y += p.vy;
          p.vy += 0.12;
          ctx.beginPath();
          ctx.fillStyle = 'rgba(230, 245, 255,' + 0.7 * p.life + ')';
          ctx.arc(p.x, p.y, p.size * p.life, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }

    function frame() {
      if (!running) return;
      drawDrops(farSetup, far);
      drawDrops(midSetup, mid);
      drawDrops(nearSetup, near);
      drawSplashes();
      rafId = window.requestAnimationFrame(frame);
    }

    rebuild();
    window.addEventListener('resize', rebuild);
    rafId = window.requestAnimationFrame(frame);

    return {
      setStormBoost: function (v) {
        stormBoost = v;
      },
      destroy: function () {
        running = false;
        window.cancelAnimationFrame(rafId);
        window.removeEventListener('resize', rebuild);
      }
    };
  }

  function createLensBeads(container) {
    if (!container || container.childElementCount) return;
    var isMobile = window.matchMedia('(max-width: 767px)').matches;
    var count = isMobile ? 18 : 32;
    var frag = document.createDocumentFragment();
    for (var i = 0; i < count; i++) {
      var bead = document.createElement('span');
      var size = 4 + Math.random() * 14;
      var drip = Math.random() > 0.5;
      bead.className = 'banner-rain__bead' + (drip ? ' banner-rain__bead--drip' : '');
      bead.style.width = size + 'px';
      bead.style.height = size * (0.9 + Math.random() * 0.45) + 'px';
      bead.style.left = Math.random() * 100 + '%';
      bead.style.top = Math.random() * 92 + '%';
      if (drip) {
        bead.style.setProperty('--drip-duration', 6 + Math.random() * 9 + 's');
        bead.style.setProperty('--drip-delay', Math.random() * 7 + 's');
        bead.style.setProperty('--drip-x', -8 + Math.random() * 16 + 'px');
        bead.style.setProperty('--drip-y', 90 + Math.random() * 170 + 'px');
      }
      frag.appendChild(bead);
    }
    container.appendChild(frag);
  }

  function jaggedBoltPath(width, height) {
    // Procedural lightning polyline in SVG viewBox space
    var x = width * (0.35 + Math.random() * 0.3);
    var y = 0;
    var parts = ['M', x.toFixed(1), y.toFixed(1)];
    var segments = 10 + Math.floor(Math.random() * 5);
    for (var i = 0; i < segments; i++) {
      x += -18 + Math.random() * 36;
      y += height / segments;
      parts.push('L', x.toFixed(1), y.toFixed(1));
      // Occasional fork
      if (Math.random() > 0.72 && i > 2 && i < segments - 2) {
        var fx = x + (Math.random() > 0.5 ? 1 : -1) * (12 + Math.random() * 24);
        var fy = y + 10 + Math.random() * 18;
        parts.push('M', x.toFixed(1), y.toFixed(1), 'L', fx.toFixed(1), fy.toFixed(1));
        parts.push('M', x.toFixed(1), y.toFixed(1));
      }
    }
    return parts.join(' ');
  }

  function startThunder(overlay, rainApi) {
    if (!overlay || prefersReducedMotion()) return;

    var flashMain = overlay.querySelector('.banner-rain__flash');
    var flashSheet = overlay.querySelector('.banner-rain__flash--sheet');
    var bolt = overlay.querySelector('.banner-rain__bolt');
    var boltPath = overlay.querySelector('.banner-rain__bolt-path');
    var boltCore = overlay.querySelector('.banner-rain__bolt-core');
    var cancelled = false;

    function setOpacity(el, value) {
      if (el) el.style.opacity = String(value);
    }

    function placeBolt() {
      if (!bolt || !boltPath) return;
      var side = Math.random() > 0.45 ? 52 + Math.random() * 28 : 18 + Math.random() * 22;
      bolt.style.left = side + '%';
      var d = jaggedBoltPath(100, 160);
      boltPath.setAttribute('d', d);
      if (boltCore) boltCore.setAttribute('d', d);
    }

    async function strike() {
      if (cancelled) return;

      // 1) Sky dims — tension before the hit
      overlay.classList.add('is-dimming');
      if (rainApi) rainApi.setStormBoost(1.25);
      await sleep(520 + Math.random() * 280);

      // 2) Bolt + flash + shake (overlay only — page stays still)
      placeBolt();
      overlay.classList.add('is-thundering', 'is-bolt', 'is-storm-shake');
      if (rainApi) rainApi.setStormBoost(1.85);

      setOpacity(flashMain, 0.85);
      setOpacity(flashSheet, 0.55);
      await sleep(70);
      setOpacity(flashMain, 0.1);
      setOpacity(flashSheet, 0.05);
      overlay.classList.remove('is-bolt');
      await sleep(80 + Math.random() * 60);

      // Double strike
      overlay.classList.add('is-bolt');
      setOpacity(flashMain, 0.7);
      setOpacity(flashSheet, 0.4);
      await sleep(55);
      setOpacity(flashMain, 0.08);
      setOpacity(flashSheet, 0.04);
      overlay.classList.remove('is-bolt');
      await sleep(45);

      if (Math.random() > 0.4) {
        overlay.classList.add('is-bolt');
        setOpacity(flashMain, 0.45);
        await sleep(40);
        overlay.classList.remove('is-bolt');
      }

      // 3) Afterglow + release dim
      setOpacity(flashMain, 0.14);
      setOpacity(flashSheet, 0.08);
      await sleep(140);
      setOpacity(flashMain, 0);
      setOpacity(flashSheet, 0);

      overlay.classList.remove('is-thundering', 'is-bolt', 'is-dimming', 'is-storm-shake');
      if (rainApi) rainApi.setStormBoost(1);
    }

    async function loop() {
      while (!cancelled) {
        await sleep(7000 + Math.random() * 8000);
        if (cancelled) break;
        await strike();
      }
    }

    setTimeout(function () {
      loop();
    }, 3200);
  }

  function initRoot(root) {
    var overlay = root.querySelector('.banner-rain');
    if (!overlay) return;
    root.appendChild(overlay);

    if (root.getAttribute('data-banner-rain-ready') === 'true') return;
    root.setAttribute('data-banner-rain-ready', 'true');

    createLensBeads(overlay.querySelector('.banner-rain__lens'));
    var rainApi = startStormRain(overlay);
    startThunder(overlay, rainApi);
  }

  function boot() {
    var roots = document.querySelectorAll('.site-monsoon-rain, .bannerslider-mainsection--rain');
    for (var i = 0; i < roots.length; i++) initRoot(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  if (window.jQuery) {
    window.jQuery(function () {
      setTimeout(boot, 50);
    });
  }
})();
